import React from 'react'
import { Col, Row, Button, Badge } from 'reactstrap'
import { withFirebase } from '../../components/Fb'
import moment from 'moment-timezone'
import 'moment/locale/fi'

import {
  NEWS_STATE_TRANSLATIONS,
  NEWS_STATE_COLORS,
} from '../../components/Inputs/schemas/news'
import {
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Paper,
  TextField,
  Checkbox,
  FormControlLabel,
  IconButton,
  Chip,
} from '@mui/material'
import { DataGrid } from '@mui/x-data-grid'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import PublishIcon from '@mui/icons-material/Publish'
import ScheduleIcon from '@mui/icons-material/Schedule'
import SaveIcon from '@mui/icons-material/Save'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ImageIcon from '@mui/icons-material/Image'
import { withAuthentication } from '../../components/Session'
import TinyEditor from '../../components/Editor/TinyEditor'
import ImageSelector from '../../components/Inputs/ImageSelector'

function TagsInput({ tags, onChange }) {
  const [inputValue, setInputValue] = React.useState('')

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault()
      const newTag = inputValue.trim()
      if (newTag && !tags.includes(newTag)) {
        onChange([...tags, newTag])
      }
      setInputValue('')
    }
  }

  const handleDelete = (tagToDelete) => {
    onChange(tags.filter((tag) => tag !== tagToDelete))
  }

  return (
    <div>
      <TextField
        label="Lisää tagi"
        value={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={handleKeyDown}
        size="small"
        fullWidth
        helperText="Paina Enter lisätäksesi tagin"
      />
      <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {tags.map((tag, index) => (
          <Chip
            key={index}
            label={tag}
            onDelete={() => handleDelete(tag)}
            size="small"
          />
        ))}
      </div>
    </div>
  )
}

function NewsEditor({ article, images, onSave, onClose, onPublish, onUnpublish, onSchedule, onDelete, processing }) {
  // Stable key for TinyEditor - set once when component mounts, doesn't change on save
  const editorKeyRef = React.useRef(article?.id || `new-${Date.now()}`)

  const [localArticle, setLocalArticle] = React.useState(article || {
    title: '',
    excerpt: '',
    content: '',
    tags: [],
    showOnUserUI: true,
    publishDate: '',
    featuredImage: '',
    featuredImageUrl: '',
  })
  const [isEdited, setIsEdited] = React.useState(false)
  const [imageDialogOpen, setImageDialogOpen] = React.useState(false)
  const [scheduleDialogOpen, setScheduleDialogOpen] = React.useState(false)
  const [scheduleDate, setScheduleDate] = React.useState(localArticle.publishDate || '')

  // Sync article ID and state from parent after save/publish/unpublish (without resetting content)
  React.useEffect(() => {
    if (article?.id && article.id !== localArticle.id) {
      setLocalArticle(prev => ({ ...prev, id: article.id, state: article.state }))
    } else if (article?.id && article.state !== localArticle.state) {
      // Sync state changes (e.g., after unpublish)
      setLocalArticle(prev => ({ ...prev, state: article.state }))
    }
  }, [article?.id, article?.state, localArticle.id, localArticle.state])

  const handleChange = (field, value) => {
    setLocalArticle({ ...localArticle, [field]: value })
    setIsEdited(true)
  }

  const handleContentChange = (content) => {
    setLocalArticle({ ...localArticle, content })
    setIsEdited(true)
  }

  const handleSave = () => {
    onSave(localArticle)
    setIsEdited(false)
  }

  const handleImageSelect = (e) => {
    const imageId = e.target.value
    const image = images.find((img) => img.id === imageId)
    if (image) {
      setLocalArticle({
        ...localArticle,
        featuredImage: imageId,
        featuredImageUrl: image.url,
      })
      setIsEdited(true)
    }
    setImageDialogOpen(false)
  }

  const handleSchedule = () => {
    if (scheduleDate) {
      onSchedule(localArticle.id, scheduleDate)
      setScheduleDialogOpen(false)
    }
  }

  const getThumbnailUrl = (url) => {
    if (!url) return null
    const parts = url.split('/')
    const filename = parts.pop()
    return [...parts, '150_' + filename].join('/')
  }

  const isDraft = !localArticle.id || localArticle.state === 'draft'
  const isScheduled = localArticle.state === 'scheduled'
  const isPublished = localArticle.state === 'published'

  return (
    <div style={{ padding: 20 }}>
      {/* Back button */}
      <div style={{ marginBottom: 16 }}>
        <Button color="secondary" onClick={onClose}>
          <ArrowBackIcon style={{ marginRight: 4 }} /> Takaisin
        </Button>
      </div>

      {/* Header */}
      <div style={{ marginBottom: 16 }}>
        <Typography variant="h6">
          {localArticle.id ? 'Muokkaa artikkelia' : 'Uusi artikkeli'}
          {localArticle.state && (
            <Badge color={NEWS_STATE_COLORS[localArticle.state]} style={{ marginLeft: 8 }}>
              {NEWS_STATE_TRANSLATIONS[localArticle.state]}
            </Badge>
          )}
        </Typography>
      </div>

      {/* Show on UI + Tags */}
      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={localArticle.showOnUserUI || false}
                  onChange={(e) => handleChange('showOnUserUI', e.target.checked)}
                  size="small"
                />
              }
              label="Näytä asiakkaille"
            />
          </Grid>
          <Grid item xs={12} md={8}>
            <TagsInput
              tags={localArticle.tags || []}
              onChange={(tags) => handleChange('tags', tags)}
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Title */}
      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <TextField
          label="Otsikko"
          value={localArticle.title || ''}
          onChange={(e) => handleChange('title', e.target.value)}
          fullWidth
          required
        />
      </Paper>

      {/* Featured Image */}
      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
          Artikkelikuva
        </Typography>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {localArticle.featuredImageUrl ? (
            <img
              src={getThumbnailUrl(localArticle.featuredImageUrl)}
              alt="Artikkelikuva"
              style={{ width: 200, height: 120, objectFit: 'cover', borderRadius: 4 }}
            />
          ) : (
            <div
              style={{
                width: 200,
                height: 120,
                backgroundColor: '#f0f0f0',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 4,
              }}
            >
              <ImageIcon color="disabled" style={{ fontSize: 48 }} />
            </div>
          )}
          <Button size="sm" onClick={() => setImageDialogOpen(true)}>
            {localArticle.featuredImageUrl ? 'Vaihda kuva' : 'Valitse kuva'}
          </Button>
        </div>
      </Paper>

      {/* Excerpt / Ingress */}
      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <TextField
          label="Ingressi"
          value={localArticle.excerpt || ''}
          onChange={(e) => handleChange('excerpt', e.target.value)}
          fullWidth
          multiline
          minRows={5}
          helperText="Lyhyt kuvaus - näytetään listauksissa ja esikatseluissa"
        />
      </Paper>

      <Paper style={{ padding: 16, marginBottom: 16 }}>
        <Typography variant="h6" style={{ marginBottom: 12 }}>
          Sisältö
        </Typography>
        <TinyEditor
          key={editorKeyRef.current}
          value={localArticle.content || ''}
          onChange={handleContentChange}
          disableSave={true}
        />
      </Paper>

      <Paper style={{ padding: 16 }}>
        <Grid container spacing={2} justifyContent="space-between" alignItems="center">
          <Grid item>
            {localArticle.id && (
              <Button
                color="danger"
                onClick={() => onDelete(localArticle.id)}
              >
                <DeleteIcon style={{ marginRight: 4 }} /> Poista
              </Button>
            )}
          </Grid>
          <Grid item>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                color="primary"
                onClick={handleSave}
                disabled={!isEdited && !!localArticle.id}
              >
                <SaveIcon style={{ marginRight: 4 }} /> Tallenna
              </Button>
              {localArticle.id && (isDraft || isScheduled) && (
                <>
                  <Button
                    color="info"
                    onClick={() => setScheduleDialogOpen(true)}
                    disabled={processing}
                  >
                    <ScheduleIcon style={{ marginRight: 4 }} /> Ajasta
                  </Button>
                  <Button
                    color="success"
                    onClick={() => onPublish(localArticle.id)}
                    disabled={processing}
                  >
                    {processing ? (
                      <CircularProgress size={16} style={{ marginRight: 4 }} />
                    ) : (
                      <PublishIcon style={{ marginRight: 4 }} />
                    )}
                    Julkaise nyt
                  </Button>
                </>
              )}
              {localArticle.id && isPublished && (
                <Button
                  color="warning"
                  onClick={() => onUnpublish(localArticle.id)}
                  disabled={processing}
                >
                  {processing ? (
                    <CircularProgress size={16} style={{ marginRight: 4 }} />
                  ) : null}
                  Palauta luonnokseksi
                </Button>
              )}
            </div>
          </Grid>
        </Grid>
      </Paper>

      {/* Image Selection Dialog */}
      <Dialog
        fullWidth
        maxWidth="md"
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
      >
        <DialogTitle>Valitse artikkelikuva</DialogTitle>
        <DialogContent>
          <ImageSelector images={images} onSelect={handleImageSelect} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImageDialogOpen(false)}>Peruuta</Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Dialog */}
      <Dialog open={scheduleDialogOpen} onClose={() => setScheduleDialogOpen(false)}>
        <DialogTitle>Ajasta julkaisu</DialogTitle>
        <DialogContent>
          <TextField
            label="Julkaisupäivä"
            type="date"
            value={scheduleDate}
            onChange={(e) => setScheduleDate(e.target.value)}
            fullWidth
            InputLabelProps={{ shrink: true }}
            style={{ marginTop: 8 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setScheduleDialogOpen(false)}>Peruuta</Button>
          <Button color="info" onClick={handleSchedule} disabled={!scheduleDate}>
            <ScheduleIcon style={{ marginRight: 4 }} /> Ajasta
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  )
}

class NewsContainerClass extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      loading: true,
      articles: [],
      images: [],
      selectedArticle: null,
      editorOpen: false,
      processing: false,
    }
  }

  componentDidMount() {
    this.loadArticles()
    this.loadImages()
  }

  loadArticles = async () => {
    this.setState({ loading: true })
    try {
      const articles = await this.props.firebase.getNewsArticles()
      this.setState({ articles, loading: false })
    } catch (error) {
      console.error('Error loading news articles:', error)
      this.setState({ loading: false })
    }
  }

  loadImages = () => {
    this.props.firebase.listenMedia().onSnapshot((snapshot) => {
      const images = []
      snapshot.forEach((doc) => {
        if (doc.data().mimetype && doc.data().mimetype.indexOf('image/') > -1) {
          images.push({ id: doc.id, ...doc.data() })
        }
      })
      this.setState({ images })
    })
  }

  handleOpenEditor = (articleId = null) => {
    if (articleId) {
      const article = this.state.articles.find((a) => a.id === articleId)
      this.setState({ selectedArticle: article, editorOpen: true })
    } else {
      this.setState({
        selectedArticle: {
          title: '',
          excerpt: '',
          content: '',
          tags: [],
          showOnUserUI: true,
          publishDate: '',
          featuredImage: '',
          featuredImageUrl: '',
          state: 'draft',
        },
        editorOpen: true,
      })
    }
  }

  handleCloseEditor = () => {
    this.setState({ editorOpen: false, selectedArticle: null })
    this.loadArticles() // Refresh list when closing editor
  }

  handleSaveArticle = async (article) => {
    this.setState({ processing: true })
    try {
      if (article.id) {
        await this.props.firebase.updateNewsArticle(article)
        this.setState({ processing: false })
      } else {
        const result = await this.props.firebase.createNewsArticle(article)
        // Update selectedArticle with new ID - don't reload articles while editing
        this.setState({
          selectedArticle: { ...article, id: result.id, state: 'draft' },
          processing: false
        })
      }
    } catch (error) {
      console.error('Error saving article:', error)
      this.setState({ processing: false })
    }
  }

  handleDeleteArticle = async (articleId) => {
    if (!window.confirm('Haluatko varmasti poistaa tämän artikkelin?')) return
    this.setState({ processing: true })
    try {
      await this.props.firebase.deleteNewsArticle(articleId)
      await this.loadArticles()
      this.handleCloseEditor()
    } catch (error) {
      console.error('Error deleting article:', error)
    }
    this.setState({ processing: false })
  }

  handlePublishArticle = async (articleId) => {
    this.setState({ processing: true })
    try {
      await this.props.firebase.publishNewsArticle(articleId)
      await this.loadArticles()
      const updatedArticle = this.state.articles.find((a) => a.id === articleId)
      if (updatedArticle) {
        this.setState({ selectedArticle: { ...updatedArticle, state: 'published' } })
      }
    } catch (error) {
      console.error('Error publishing article:', error)
    }
    this.setState({ processing: false })
  }

  handleScheduleArticle = async (articleId, publishDate) => {
    this.setState({ processing: true })
    try {
      await this.props.firebase.scheduleNewsArticle(articleId, publishDate)
      await this.loadArticles()
      const updatedArticle = this.state.articles.find((a) => a.id === articleId)
      if (updatedArticle) {
        this.setState({ selectedArticle: { ...updatedArticle, state: 'scheduled', publishDate } })
      }
    } catch (error) {
      console.error('Error scheduling article:', error)
    }
    this.setState({ processing: false })
  }

  handleUnpublishArticle = async (articleId) => {
    this.setState({ processing: true })
    try {
      await this.props.firebase.unpublishNewsArticle(articleId)
      this.setState({ selectedArticle: { ...this.state.selectedArticle, state: 'draft' } })
    } catch (error) {
      console.error('Error unpublishing article:', error)
    }
    this.setState({ processing: false })
  }

  getColumns = () => {
    return [
      {
        headerName: 'Otsikko',
        field: 'title',
        flex: 2,
        minWidth: 200,
      },
      {
        headerName: 'Tila',
        field: 'state',
        flex: 1,
        minWidth: 100,
        renderCell: (params) => {
          const color = NEWS_STATE_COLORS[params.value] || 'secondary'
          const label = NEWS_STATE_TRANSLATIONS[params.value] || params.value
          return <Badge color={color}>{label}</Badge>
        },
      },
      {
        headerName: 'Tagit',
        field: 'tags',
        flex: 1,
        minWidth: 150,
        renderCell: (params) => {
          const tags = params.value || []
          return (
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              {tags.slice(0, 3).map((tag, index) => (
                <Chip key={index} label={tag} size="small" />
              ))}
              {tags.length > 3 && <Chip label={`+${tags.length - 3}`} size="small" />}
            </div>
          )
        },
      },
      {
        headerName: 'Julkaisupäivä',
        field: 'publishDate',
        flex: 1,
        minWidth: 120,
        valueFormatter: (value) => {
          if (!value) return '-'
          return moment(value).format('D.M.YYYY')
        },
      },
      {
        headerName: 'Luotu',
        field: 'created',
        flex: 1,
        minWidth: 120,
        valueFormatter: (value) => {
          if (!value || !value.toDate) return '-'
          return moment(value.toDate()).format('D.M.YYYY')
        },
      },
      {
        headerName: 'Toiminnot',
        field: 'actions',
        flex: 1,
        minWidth: 120,
        sortable: false,
        renderCell: (params) => {
          return (
            <div>
              <IconButton
                size="small"
                onClick={() => this.handleOpenEditor(params.row.id)}
                title="Muokkaa"
              >
                <EditIcon fontSize="small" />
              </IconButton>
              <IconButton
                size="small"
                onClick={() => this.handleDeleteArticle(params.row.id)}
                title="Poista"
                color="error"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </div>
          )
        },
      },
    ]
  }

  render() {
    const { loading, articles, images, editorOpen, selectedArticle, processing } = this.state

    if (loading) {
      return (
        <div className="content">
          <Row>
            <Col className="text-center">
              <CircularProgress />
              <Typography>Ladataan uutisia...</Typography>
            </Col>
          </Row>
        </div>
      )
    }

    if (editorOpen) {
      return (
        <div className="content">
          <NewsEditor
            article={selectedArticle}
            images={images}
            onSave={this.handleSaveArticle}
            onClose={this.handleCloseEditor}
            onPublish={this.handlePublishArticle}
            onUnpublish={this.handleUnpublishArticle}
            onSchedule={this.handleScheduleArticle}
            onDelete={this.handleDeleteArticle}
            processing={processing}
          />
        </div>
      )
    }

    return (
      <div className="content">
        <Row>
          <Col>
            <Button
              disabled={processing}
              color="primary"
              onClick={() => this.handleOpenEditor()}
            >
              <AddIcon style={{ marginRight: 4 }} /> Luo uusi artikkeli
            </Button>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col>
            <Typography variant="caption">
              Uutiset näytetään asiakaskäyttöliittymässä. Luo artikkeli, lisää sisältö ja julkaise se.
            </Typography>
          </Col>
        </Row>
        <Row className="mt-3">
          <Col md="12">
            <div style={{ display: 'flex', minHeight: 'calc(100vh - 250px)', backgroundColor: '#FFFFFF' }}>
              <div style={{ flexGrow: 1 }}>
                <DataGrid
                  rows={articles}
                  columns={this.getColumns()}
                  density="compact"
                  initialState={{
                    sorting: {
                      sortModel: [{ field: 'created', sort: 'desc' }],
                    },
                  }}
                  disableRowSelectionOnClick
                />
              </div>
            </div>
          </Col>
        </Row>
      </div>
    )
  }
}

export default withAuthentication(withFirebase(NewsContainerClass))
